import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Star, ChevronLeft, ChevronRight, Quote, MapPin } from 'lucide-react';
import { testimonials as fallbackTestimonials } from '../mock';
import { API } from '../api';
import { useSettings } from '../store';

const parseRole = (role = '') => {
  const parts = String(role).split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) return { tripType: parts[0], location: parts.slice(1).join(', ') };
  return { tripType: parts[0] || '', location: '' };
};

const Testimonials = () => {
  const ref = useRef(null);
  const settings = useSettings();
  const [items, setItems] = useState(fallbackTestimonials);

  useEffect(() => {
    axios.get(`${API}/testimonials`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setItems(res.data);
      })
      .catch(() => {});
  }, []);

  const scroll = (dir) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
  };

  return (
    <section className="py-16 bg-white" data-testid="testimonials-section">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900">
            {settings.section_testimonials_title || 'What Our Travellers Say'}
          </h2>
          <p className="text-sm text-gray-500 mt-2">Real stories from real journeys with Travelsera</p>
        </div>

        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0d64a8] text-white items-center justify-center shadow-lg hover:bg-[#0a548e] transition"
            aria-label="prev"
            data-testid="testimonials-prev-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => scroll(1)}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0d64a8] text-white items-center justify-center shadow-lg hover:bg-[#0a548e] transition"
            aria-label="next"
            data-testid="testimonials-next-btn"
          >
            <ChevronRight size={20} />
          </button>

          <div ref={ref} className="flex gap-6 overflow-x-auto scroll-smooth snap-x pb-4" style={{ scrollbarWidth: 'none' }}>
            {items.map((t, i) => {
              const { tripType, location } = parseRole(t.role);
              const avatarSrc = t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=0d64a8&color=fff&size=128`;
              return (
                <div
                  key={t.id || i}
                  data-testid={`testimonial-card-${i}`}
                  className="snap-start shrink-0 w-[300px] md:w-[360px] bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Upper section: destination bg image with avatar, name, trip type, location, rating */}
                  <div className="relative h-52 bg-gradient-to-br from-[#0d64a8] to-[#0a2a4a]">
                    {t.destination_image && (
                      <img
                        src={t.destination_image}
                        alt={`${t.name} destination`}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/30" />

                    <div className="absolute inset-0 p-4 flex flex-col justify-between">
                      {/* Top row: rating badge */}
                      <div className="flex justify-end">
                        <div className="flex items-center gap-0.5 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow" data-testid={`testimonial-rating-${i}`}>
                          {Array.from({ length: t.rating || 5 }).map((_, idx) => (
                            <Star key={idx} size={11} className="fill-[#ffcc29] text-[#ffcc29]" />
                          ))}
                        </div>
                      </div>

                      {/* Bottom row: avatar + name/trip/location */}
                      <div className="flex items-end gap-3">
                        <img
                          src={avatarSrc}
                          alt={t.name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-lg shrink-0"
                          onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=0d64a8&color=fff&size=128`; }}
                          data-testid={`testimonial-avatar-${i}`}
                        />
                        <div className="min-w-0 pb-0.5">
                          <h4 className="font-semibold text-white text-base leading-tight truncate drop-shadow" data-testid={`testimonial-name-${i}`}>
                            {t.name}
                          </h4>
                          {tripType && (
                            <p className="text-[11px] uppercase tracking-wider text-[#ffcc29] font-semibold mt-0.5 truncate drop-shadow">
                              {tripType}
                            </p>
                          )}
                          {location && (
                            <div className="flex items-center gap-1 mt-0.5 text-white/90 text-xs truncate drop-shadow">
                              <MapPin size={11} className="shrink-0" />
                              <span className="truncate">{location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lower section: only the testimonial review */}
                  <div className="p-5 bg-[#f7fafc] relative">
                    <Quote className="absolute top-3 right-4 text-[#0d64a8]/15" size={40} />
                    <p className="text-sm text-gray-700 leading-relaxed relative z-10" data-testid={`testimonial-text-${i}`}>
                      &ldquo;{t.text}&rdquo;
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
